import { invoke } from '@shared/easyfy.decorators';
import { element, elementsOf, globalEvent } from '@shared/easyfy.core';
import { addCss, replaceHtml, toggleClass } from '@shared/easyfy.utils';
import { animationEnded, isMobile } from '@clientShared/utils';

@invoke
export default class Dropdown {
  static Instance: Dropdown;
  sliderTranslate: string = '';

  constructor() {
    Dropdown.Instance = this;
  }

  onInit() {
    globalEvent({
      type: 'click',
      events: [
        {
          event: 'select-sort-option',
          callback: this.onSortOptionClick,
        },
        {
          event: 'dd-toggle',
          callback: this.onVariantSelectorToggle.bind(this),
        },
        {
          event: 'dd-select',
          callback: this.onVariantSelectorOptionClick,
        },
        {
          event: 'dd-close',
          callback: this.closeVariantSelector.bind(this),
        },
        {
          callback: this.closeVariantSelector,
        },
      ],
    });

    document.addEventListener('close-variant-selector', () => {
      this.closeVariantSelector();
    });
  }

  onSortOptionClick(e) {
    e.preventDefault();

    let target = e?.target as HTMLElement;
    const parent = target?.parentElement?.parentElement;

    if (parent && target) {
      const optionValue = target.attributes['data-value']?.value;
      const optionText = target.querySelector("[data-container='sort-option-text']")?.innerHTML;
      const selectedElement = parent.querySelector("[data-container='selected-sort-option']") as HTMLElement;

      if (selectedElement) {
        selectedElement.innerHTML = optionText;
        selectedElement.dataset['value'] = optionValue;
      }

      parent.removeAttribute('open');
      parent.classList.remove('open');

      const customEvent = selectedElement?.attributes['data-custom-event']?.value;

      if (customEvent) {
        const event = new Event(customEvent, { bubbles: true, cancelable: true });
        selectedElement?.dispatchEvent(event);
      }
    }

    return false;
  }

  onVariantSelectorToggle(e: Event) {
    Dropdown.Instance.closeVariantSelector();

    const element: HTMLElement = e?.target as HTMLElement;
    const parent: HTMLElement = element.closest('[data-container="product-card"]');
    const menu: HTMLElement = parent.querySelector('.dd-salesunit-menu');
    const selector: HTMLElement = parent.querySelector('.selector');
    const slider: HTMLElement = element.closest('[data-container="productlist-scroll"]');

    const url: string = (parent.querySelector('a.product-info') as HTMLAnchorElement)?.href ?? null;
    const isCompact: boolean = parent.hasAttribute('product-card-compact');

    if (slider && isMobile()) this.disableSlider(slider);
    if (url && isMobile) this.createLink(url, menu);
    if (!parent || !menu || !selector) return;
    if (!isMobile()) addCss(selector, 'desktop');
    if (isCompact) element.dataset.event = 'dd-close';

    if (!menu.hasAttribute('dd-open')) {
      if (isMobile() && !isCompact) {
        // show salesunit selector buttom
        let salesunitContainer: HTMLElement = document.querySelector('.salesunitselector-mobile-container');
        let appendToBody = false;
        if (!salesunitContainer) {
          salesunitContainer = document.createElement('div');
          salesunitContainer.classList.add('salesunitselector-mobile-container', 'productcard-regular', 'dd-open');
          salesunitContainer.setAttribute('data-container', 'product-card');
          appendToBody = true;
        } else {
          salesunitContainer.innerText = '';
          salesunitContainer.classList.add('dd-open');
        }

        const menuClone: HTMLElement = menu.cloneNode(true) as HTMLElement;
        menuClone.setAttribute('dd-open', '');
        menuClone.removeAttribute('dd-closing');

        const button: HTMLElement =
          parent.querySelector('[data-container="addtocart-btn"]') ??
          parent.querySelector('[data-container="view-product"]') ??
          parent.querySelector('[data-action="subscribe"]');
        if (button) {
          menuClone.insertAdjacentHTML('beforeend', button.outerHTML);
        }

        salesunitContainer.appendChild(menuClone);

        if (appendToBody) {
          document.body.appendChild(salesunitContainer);
        }

        document.body.classList.add('noof');
      } else {
        toggleClass(parent, 'dd-open');
        menu.setAttribute('dd-open', '');
      }
    }
  }

  createLink = (url: string, menu: HTMLElement) => {
    const link = document.createElement('a');
    link.href = url;
    const productInfo = menu.querySelector('.product-info');
    link.innerHTML = productInfo.innerHTML;
    addCss(link, 'product-info');
    productInfo.parentNode.replaceChild(link, productInfo);
  };

  onVariantSelectorOptionClick(e) {
    const element: HTMLElement = e?.target;
    const productId: string = element.dataset.value;
    const salesUnitCode: string = element.dataset.salesunitcode;
    const upsellId: string = element.dataset.upsellid;
    const parent: HTMLElement = element.closest('[data-container="product-card"]') ?? element.closest('[data-container="product"]');
    const buyButtons: NodeListOf<HTMLElement> = parent?.querySelectorAll('[data-container="addtocart-btn"]');
    const selectedVariant: HTMLElement = parent ? elementsOf(parent, '[data-container="selected-variant"]') : null;
    const prodPageActionElement: HTMLElement = parent ? elementsOf(parent, '.pp-actions') : null;
    const priceContainer: HTMLElement = parent ? parent.querySelector('[data-container="price-container"]') : null;
    const isCompact: boolean = parent.hasAttribute('product-card-compact');
    const flags: NodeListOf<HTMLElement> = parent?.querySelectorAll('.flag[data-salesunit]');

    Dropdown.Instance.removeSelectedVariant(element);
    element.setAttribute('selected', 'selected');
    Dropdown.Instance.updateBuyButtonValue(buyButtons, productId, salesUnitCode, upsellId);
    Dropdown.Instance.updateFlagVisibility(flags, salesUnitCode);

    if (!prodPageActionElement) Dropdown.Instance.updateHtml(selectedVariant, element, isCompact, priceContainer);
    if (prodPageActionElement) Dropdown.Instance.updateProductPageActionInfo(parent, prodPageActionElement);

    if (!isMobile() || isCompact) Dropdown.Instance.closeVariantSelector();
  }

  closeVariantSelector(e?: Event) {
    const openProductCard: HTMLElement = element('[data-container="product-card"].dd-open');

    if (!openProductCard) return false;

    const target = e?.target as HTMLElement | undefined;

    if (target && target.hasAttribute('dd-open')) return;

    const isProductCardCompact: boolean = openProductCard?.hasAttribute('product-card-compact');
    const selectedVariant: HTMLElement = openProductCard?.querySelector('[data-container="selected-variant"]');
    const slider: HTMLElement = element('[data-container="productlist-scroll"].disable-slider');
    const menu: HTMLElement = openProductCard.querySelector('.dd-salesunit-menu');
    const button: HTMLElement = menu.querySelector('[data-container="addtocart-btn"]');

    if (!menu) return;

    if (isProductCardCompact && selectedVariant) {
      menu.removeAttribute('dd-open');
      openProductCard.classList.remove('dd-open');
      selectedVariant.dataset.event = 'dd-toggle';
      return;
    }

    menu.setAttribute('dd-closing', '');
    animationEnded(menu, () => {
      menu.removeAttribute('dd-closing');
      menu.removeAttribute('dd-open');
      if (isMobile()) {
        document.body.classList.remove('noof');
        button?.remove();
      }
      toggleClass(openProductCard, 'dd-open');
      if (slider && isMobile()) this.enableSlider(slider);
    });
  }

  removeSelectedVariant = (element: HTMLElement) => {
    const selectedVariant: HTMLElement = elementsOf(element?.parentElement, '[data-container="dd-option"][selected]');
    if (selectedVariant) selectedVariant.removeAttribute('selected');
  };

  updateBuyButtonValue = (buyButtons: NodeListOf<HTMLElement>, productId: string, salesUnitCode: string, upsellId: string) => {
    if (!buyButtons || !productId) return;

    buyButtons.forEach((button) => {
      button.dataset.productid = productId;
      button.dataset.salesunitcode = salesUnitCode;
      button.dataset.upsellid = upsellId;
    });
  };

  updateFlagVisibility = (flags: NodeListOf<HTMLElement>, salesUnitCode: string) => {
    if (!flags || !salesUnitCode) return;

    const currentVisibleFlags = Array.from(flags).filter((flag) => flag.hasAttribute('data-current'));
    currentVisibleFlags.forEach((flag) => {
      flag.removeAttribute('data-current');
    });

    const newVisibleFlags = Array.from(flags).filter((flag) => flag.getAttribute('data-salesunit') === salesUnitCode);
    newVisibleFlags.forEach((flag) => {
      flag.setAttribute('data-current', 'true');
    });
  };

  updateHtml = (selectedVariant: HTMLElement, element: HTMLElement, isSearch: boolean, priceContainer: HTMLElement) => {
    if (!selectedVariant || !element) return;

    selectedVariant.innerHTML = element.innerHTML;
    priceContainer.attributes['data-unitprice'].value = element.querySelector('[data-container="unit-price"]')?.innerHTML;
    selectedVariant.dataset.value = element.dataset.value;
    const newPacksize = selectedVariant.querySelector('[data-container="packsize"]').attributes['data-packsize']?.value;

    if (newPacksize) priceContainer.attributes['data-packsize'].value = newPacksize;

    if (!isSearch) {
      const packsize: HTMLElement = elementsOf(selectedVariant, '.packsize');
      const packsizehtml = `<span class="packsize">${packsize?.innerHTML}<i class="icon icon-chevron-down size-xs clr-black"></i></span>`;
      replaceHtml(packsize, packsizehtml);
    } else {
      const price: HTMLElement = elementsOf(selectedVariant, '.price');
      const pricehtml = `<span class="price"><span data-container="price">${price?.innerHTML}</span><i class="icon icon-chevron-down size-xs clr-black"></i></span>`;
      replaceHtml(price, pricehtml);
    }
  };

  updateProductPageActionInfo = (parent: HTMLElement, prodPageActionElement: HTMLElement) => {
    const variants: HTMLElement = elementsOf(parent, '.selector.productpage');
    const selectedVariant: HTMLElement = elementsOf(variants, '[selected="selected"]');
    if (!selectedVariant) return;
    const selectedPackSize: HTMLElement = elementsOf(selectedVariant, '.packsize');
    const selectedPrice: HTMLElement = elementsOf(selectedVariant, '.price');
    const price: HTMLElement = elementsOf(prodPageActionElement, '.price');
    const packsize: HTMLElement = elementsOf(prodPageActionElement, '.packsize');
    if (price) {
      price.innerText = selectedPrice.innerText;
    }
    if (packsize) {
      packsize.innerText = selectedPackSize.innerText;
    }
  };

  disableSlider = (slider: HTMLElement) => {
    toggleClass(slider, 'disable-slider');
    this.sliderTranslate = (slider.querySelector('.splide__list') as HTMLElement)?.getAttribute('style')?.toString();
  };

  enableSlider = (slider: HTMLElement) => {
    toggleClass(slider, 'disable-slider');
    (slider.querySelector('.splide__list') as HTMLElement)?.setAttribute('style', this.sliderTranslate);
  };
}
